import React, { useMemo, useState } from 'react';
import { Input, Select, Table, Tag } from 'antd';

import { DashboardOrg } from '@totem/components/ticketing/ticketDashboard/types';

type Props = {
  orgs: Record<string, DashboardOrg>;
  onOrgSelect: (id: string) => void;
};

const CustomersTab = ({ orgs, onOrgSelect }: Props) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('open');

  const rows = useMemo(() => {
    let list = Object.values(orgs);
    if (search) {
      const query = search.toLowerCase();
      list = list.filter((org) => org.name.toLowerCase().includes(query));
    }

    const sorters: Record<
      string,
      (orgA: DashboardOrg, orgB: DashboardOrg) => number
    > = {
      open: (orgA, orgB) => orgB.m.open - orgA.m.open,
      p1: (orgA, orgB) => orgB.m.p1 - orgA.m.p1,
      br: (orgA, orgB) => orgB.m.br - orgA.m.br,
      oldest: (orgA, orgB) => orgB.m.oldest - orgA.m.oldest,
      net30: (orgA, orgB) => orgB.m.net30 - orgA.m.net30,
      name: (orgA, orgB) => orgA.name.localeCompare(orgB.name),
    };

    return list.sort(sorters[sortKey] ?? sorters.open);
  }, [orgs, search, sortKey]);

  const columns = [
    {
      title: 'Customer',
      key: 'name',
      render: (_: unknown, org: DashboardOrg) => (
        <div>
          <strong
            style={{ cursor: 'pointer', color: '#1E3A5F' }}
            onClick={() => onOrgSelect(org.id)}
          >
            {org.name}
          </strong>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>{org.type}</div>
        </div>
      ),
    },
    {
      title: 'Open',
      key: 'open',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) =>
        org.m.open === 0 ? (
          <span style={{ color: '#94A3B8' }}>0</span>
        ) : (
          <strong>{org.m.open}</strong>
        ),
    },
    {
      title: 'P1',
      key: 'p1',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) =>
        org.m.p1 > 0 ? (
          <span style={{ color: '#9C2A2A', fontWeight: 700 }}>{org.m.p1}</span>
        ) : (
          0
        ),
    },
    {
      title: 'Past SLA',
      key: 'br',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) =>
        org.m.br > 0 ? <Tag color="error">{org.m.br}</Tag> : 0,
    },
    {
      title: 'Unowned',
      key: 'un',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) =>
        org.m.un > 0 ? (
          <span style={{ color: '#C88A2B', fontWeight: 700 }}>{org.m.un}</span>
        ) : (
          0
        ),
    },
    {
      title: 'Oldest (d)',
      key: 'oldest',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) => org.m.oldest.toFixed(1),
    },
    {
      title: 'Created 30d',
      dataIndex: ['m', 'c30'],
      key: 'c30',
      align: 'right' as const,
    },
    {
      title: 'Closed 30d',
      dataIndex: ['m', 'x30'],
      key: 'x30',
      align: 'right' as const,
    },
    {
      title: 'Net 30d',
      key: 'net30',
      align: 'right' as const,
      render: (_: unknown, org: DashboardOrg) => (
        <span
          style={{
            fontWeight: org.m.net30 !== 0 ? 700 : 400,
            color:
              org.m.net30 > 0
                ? '#C94442'
                : org.m.net30 < 0
                  ? '#2F9E6B'
                  : undefined,
          }}
        >
          {org.m.net30 > 0 ? `+${org.m.net30}` : org.m.net30}
        </span>
      ),
    },
    {
      title: 'Proactive',
      dataIndex: ['m', 'pr'],
      key: 'pr',
      align: 'right' as const,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input
          style={{ width: 240 }}
          placeholder="Search customers..."
          value={search}
          onChange={(evt) => setSearch(evt.target.value)}
          allowClear
        />
        <Select
          style={{ width: 160 }}
          value={sortKey}
          onChange={setSortKey}
          options={[
            { value: 'open', label: 'Sort: Open' },
            { value: 'p1', label: 'Sort: P1' },
            { value: 'br', label: 'Sort: Past SLA' },
            { value: 'oldest', label: 'Sort: Oldest' },
            { value: 'net30', label: 'Sort: Net 30d' },
            { value: 'name', label: 'Sort: Name' },
          ]}
        />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748B' }}>
          {rows.length} customer{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        onRow={(org) => ({
          style: { cursor: 'pointer' },
          onClick: () => onOrgSelect(org.id),
        })}
      />
    </div>
  );
};

export default CustomersTab;
